import dayjs from 'dayjs';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Button, Spinner } from 'ui';
import { ColumnsType } from 'antd/lib/table/interface';
import { Tag, Flex, message } from 'antd';
import { Checkbox } from 'ui/Checkbox';
import {
  StyledApplicationsTable,
  CenteredCell,
  StyledMobileApplicationCards,
} from './styles';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import { DrawerShowListingContractChanges } from 'ui/Drawer/Drawers/DrawerShowListingContractChanges';
import { DrawerViewListingApplication } from 'ui/Drawer/Drawers/DrawerViewListingApplication';
import type { ListingApplication, SplitDeliveryData } from 'types/ListingData';
import {
  ApplicationsTableProps,
  TreeTableRow,
  ExtendedApplicationProductData,
} from '../types';
import { ApplicationsInfographics } from './ApplicationsInfographics';
import {
  StyledPageLayout,
  StyledInfographicsContainer,
  StyledTableContainer,
} from '../styles';
import useQueryApiClient from '../../../../../utils/useQueryApiClient';

interface ViewApplicationRecord {
  id: number | undefined;
  status: string | undefined;
  delivery_cost: number | undefined;
  total_offer_price: number | string | null | undefined;
  order_transaction:
    | { id: number; parent_transaction_id?: number | null }
    | null
    | undefined;
  openProductsTab: boolean;
}

export const ApplicationsTable = ({
  applications,
  expandedRowKeys,
  onExpandedRowsChange,
  loading,
  filter,
  page_size = 25,
  listing,
  onActionCompleted,
  isSingleProducerOnly,
  onCreateTransactions,
  bestValues,
  initialApplicationId,
  onInitialApplicationViewed,
}: ApplicationsTableProps) => {
  const intl = useIntl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [splitDeliveries, setSplitDeliveries] = useState<SplitDeliveryData[]>(
    []
  );
  const [selectedApplication, setSelectedApplication] =
    useState<TreeTableRow | null>(null);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedApplicationForView, setSelectedApplicationForView] = useState<
    TreeTableRow | ListingApplication | ViewApplicationRecord | null
  >(null);
  const [selectedProducts, setSelectedProducts] = useState<
    Array<{
      id: number;
      listing_application_product_id: number;
      listing_application_id: number;
      selected_quantity: number;
      listing_application_product: {
        listing_product?: { id: string };
      };
    }>
  >([]);
  const [savedSelectedProducts, setSavedSelectedProducts] = useState<
    Array<{
      listing_application_product_id: number;
      listing_application_id: number;
      selected_quantity: number;
    }>
  >([]);

  const applicationsRef = useRef(applications);
  const initialApplicationIdHandled = useRef(false);

  useEffect(() => {
    if (
      initialApplicationId &&
      !initialApplicationIdHandled.current &&
      !viewDrawerOpen
    ) {
      initialApplicationIdHandled.current = true;
      const app = applications.find((a) => a.id === initialApplicationId);
      const applicationToShow = app ?? { id: initialApplicationId };
      setSelectedApplicationForView(applicationToShow);
      setViewDrawerOpen(true);
      onInitialApplicationViewed?.();
    }
  }, [
    initialApplicationId,
    applications,
    viewDrawerOpen,
    onInitialApplicationViewed,
  ]);

  useEffect(() => {
    if (applications.length > 0 && applicationsRef.current.length === 0) {
      applicationsRef.current = applications;
      setSelectedProducts((prev) => {
        const allRelevantApplicationIds = new Set<number>(
          applications.map((app) => app.id)
        );
        if (
          selectedApplicationForView &&
          'id' in selectedApplicationForView &&
          selectedApplicationForView.id
        ) {
          const viewId =
            typeof selectedApplicationForView.id === 'string'
              ? parseInt(selectedApplicationForView.id, 10)
              : selectedApplicationForView.id;
          if (!isNaN(viewId)) {
            allRelevantApplicationIds.add(viewId);
          }
        }

        const filtered = prev.filter((item) =>
          allRelevantApplicationIds.has(item.listing_application_id)
        );
        return filtered;
      });
    }
  }, [applications, selectedApplicationForView]);

  const { appendData: selectProducts } = useQueryApiClient({
    request: {
      url: '/api/v2/listing-applications/select-products',
      method: 'POST',
    },
    onSuccess: (response: { success: boolean }) => {
      message.success(
        intl.formatMessage({ id: 'listings.applications.products_selected' })
      );
      onActionCompleted?.();
      refetchSelectedProducts?.();
    },
    onError: (error: {
      message: string;
      errors?: Record<string, string[]>;
    }) => {
      const errorMessage =
        error?.message ||
        error?.errors?.action?.[0] ||
        intl.formatMessage({ id: 'message.error' });
      message.error(errorMessage);
    },
  });

  const { refetch: refetchSelectedProducts } = useQueryApiClient({
    request: {
      url: '/api/v2/listing-applications/selected-products',
      data: { listing_id: listing?.id || '' },
    },
    enabled: !!listing?.id,
    onSuccess: (data: {
      selected_products: Array<{
        id: number;
        listing_application_product_id: number;
        listing_application_id: number;
        selected_quantity: number;
        listing_application_product: {
          listing_product?: { id: string };
        };
      }>;
      split_deliveries: Array<{
        id: string;
        delivery_address: string;
        delivery_start_date?: string;
        delivery_end_date?: string;
        delivery_frequency_value?: number;
        delivery_frequency_unit?: string;
        products: Array<{
          application_id: number;
          application_product_id: number;
          quantity: number;
          offered_price?: number;
        }>;
      }>;
    }) => {
      if (
        data &&
        data.selected_products &&
        Array.isArray(data.selected_products)
      ) {
        setSelectedProducts(data.selected_products);
        setSavedSelectedProducts(
          data.selected_products.map(
            ({
              listing_application_id,
              listing_application_product_id,
              selected_quantity,
            }) => ({
              listing_application_id,
              listing_application_product_id,
              selected_quantity,
            })
          )
        );
      }
      if (
        data &&
        data.split_deliveries &&
        Array.isArray(data.split_deliveries)
      ) {
        setSplitDeliveries(data.split_deliveries);
      }
    },
    onError: (error: {
      message: string;
      errors?: Record<string, string[]>;
    }) => {},
  });

  const applicationsMap = useMemo(() => {
    const map = new Map<number, ListingApplication>();
    applications.forEach((app) => {
      if (app.id) {
        map.set(app.id, app);
      }
    });
    return map;
  }, [applications]);

  const handleViewApplication = useCallback(
    (
      application:
        | TreeTableRow
        | ListingApplication
        | ViewApplicationRecord
        | null
    ) => {
      setSelectedApplicationForView(application);
      setViewDrawerOpen(true);
    },
    []
  );

  const hasListingStructuredDelivery = useCallback(() => {
    const conditions = listing?.delivery_conditions;
    if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
      return false;
    }
    return conditions.some(
      (c: { delivery_type?: string }) =>
        c.delivery_type === 'delivery_conditions' ||
        c.delivery_type === 'detailed_delivery'
    );
  }, [listing?.delivery_conditions]);

  const hasContractChanges = useCallback(
    (record: TreeTableRow): boolean => {
      const hasProductChanges = record.application_products?.some(
        (product) => product.offered_quantity !== null
      );

      const listingHasStructuredDelivery = hasListingStructuredDelivery();

      const hasDeliveryChanges = listingHasStructuredDelivery
        ? record.delivery_conditions?.some(
            (delivery) =>
              delivery.is_accepting_all === false ||
              delivery.listing_delivery_condition_id === null
          )
        : false;

      return !!(hasProductChanges || hasDeliveryChanges);
    },
    [hasListingStructuredDelivery]
  );

  const columns: ColumnsType<TreeTableRow> = useMemo(() => {
    if (isSingleProducerOnly) {
      return [
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.producer',
          }),
          key: 'producer',
          width: '30%',
          sorter: (a: TreeTableRow, b: TreeTableRow) => {
            if (a.isProductRow || b.isProductRow) return 0;
            const aName = (a.farm_brand || a.farm_name || '').toLowerCase();
            const bName = (b.farm_brand || b.farm_name || '').toLowerCase();

            return aName.localeCompare(bName);
          },
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              const productName =
                record.product_name ||
                record.listing_product?.category?.name ||
                '';

              return <span className="product-name">{productName}</span>;
            }

            return record.farm_brand || record.farm_name || '';
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.total_price',
          }),
          key: 'total_price',
          width: '15%',
          sorter: (a: TreeTableRow, b: TreeTableRow) => {
            if (a.isProductRow || b.isProductRow) return 0;
            const aValue =
              typeof a.total_offer_price === 'string'
                ? parseFloat(a.total_offer_price)
                : a.total_offer_price || 0;
            const bValue =
              typeof b.total_offer_price === 'string'
                ? parseFloat(b.total_offer_price)
                : b.total_offer_price || 0;

            return aValue - bValue;
          },
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              const requiredQty = record.listing_product?.required_quantity;
              const quantity =
                record.offered_quantity ??
                (typeof requiredQty === 'string'
                  ? parseFloat(requiredQty)
                  : requiredQty) ??
                0;
              const price = record.offered_price ?? 0;
              const totalPrice = quantity * price;
              const unitPrice = price;
              const unit = record.listing_product?.storage_unit?.short || '';

              const hasBothBestValues =
                record.isBestTotalPrice && record.isBestUnitPrice;

              return (
                <CenteredCell>
                  {totalPrice > 0 && (
                    <>
                      {hasBothBestValues ? (
                        <div className="best-value-highlight">
                          <div>{totalPrice.toFixed(2)} €</div>
                          {unitPrice > 0 && unit && (
                            <div>
                              {unitPrice.toFixed(2)} €/{unit}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div
                            className={
                              record.isBestTotalPrice
                                ? 'best-value-highlight'
                                : undefined
                            }
                          >
                            {totalPrice.toFixed(2)} €
                          </div>
                          {unitPrice > 0 && unit && (
                            <div
                              className={
                                record.isBestUnitPrice
                                  ? 'best-value-highlight'
                                  : undefined
                              }
                            >
                              {unitPrice.toFixed(2)} €/{unit}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </CenteredCell>
              );
            }
            const numValue =
              typeof record.total_offer_price === 'string'
                ? parseFloat(record.total_offer_price)
                : record.total_offer_price;

            return numValue && !isNaN(numValue) ? (
              <CenteredCell>
                <div
                  className={
                    record.isBestApplicationTotalPrice
                      ? 'best-value-highlight'
                      : undefined
                  }
                >
                  {numValue.toFixed(2)} €
                </div>
              </CenteredCell>
            ) : (
              ''
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.quantity',
          }),
          key: 'quantity',
          width: '15%',
          sorter: (a: TreeTableRow, b: TreeTableRow) => {
            if (a.isProductRow || b.isProductRow) return 0;

            const aTotalQuantity = (a.application_products || []).reduce(
              (sum: number, product: ExtendedApplicationProductData) => {
                const requiredQty = product.listing_product?.required_quantity;
                const quantity =
                  product.offered_quantity ??
                  (typeof requiredQty === 'string'
                    ? parseFloat(requiredQty)
                    : requiredQty) ??
                  0;

                return sum + quantity;
              },
              0
            );

            const bTotalQuantity = (b.application_products || []).reduce(
              (sum: number, product: ExtendedApplicationProductData) => {
                const requiredQty = product.listing_product?.required_quantity;
                const quantity =
                  product.offered_quantity ??
                  (typeof requiredQty === 'string'
                    ? parseFloat(requiredQty)
                    : requiredQty) ??
                  0;

                return sum + quantity;
              },
              0
            );

            return aTotalQuantity - bTotalQuantity;
          },
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              const requiredQty = record.listing_product?.required_quantity;
              const quantity =
                record.offered_quantity !== null &&
                record.offered_quantity !== undefined
                  ? record.offered_quantity
                  : typeof requiredQty === 'string'
                    ? parseFloat(requiredQty) || 0
                    : requiredQty || 0;
              const unit = record.listing_product?.storage_unit?.short || '';

              return quantity > 0 ? (
                <CenteredCell>
                  <span
                    className={
                      record.isBestQuantity ? 'best-value-highlight' : undefined
                    }
                  >
                    {quantity} {unit}
                  </span>
                </CenteredCell>
              ) : (
                ''
              );
            }

            return '';
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.distance',
          }),
          key: 'distance',
          width: '15%',
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              return '';
            }
            const numValue =
              typeof record.calculated_distance_km === 'string'
                ? parseFloat(record.calculated_distance_km)
                : record.calculated_distance_km;
            return numValue && !isNaN(numValue) ? (
              <CenteredCell>
                <span
                  className={
                    record.isBestDistance ? 'best-value-highlight' : undefined
                  }
                >
                  {numValue} km
                </span>
              </CenteredCell>
            ) : (
              ''
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.contract',
          }),
          key: 'contract',
          width: '10%',
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              return '';
            }

            if (hasContractChanges(record)) {
              return (
                <Button
                  type="link"
                  label={intl.formatMessage({
                    id: 'listings.applications.contract_changes',
                  })}
                  onClick={() => {
                    setSelectedApplication(record);
                    setDrawerOpen(true);
                  }}
                  className="contract-changes-button"
                />
              );
            }

            return (
              <Checkbox
                checked={true}
                disabled={true}
                className="contract-checkbox"
              />
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.delivery_cost',
          }),
          key: 'delivery_cost',
          width: '15%',
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              return '';
            }

            const deliveryCost = Number(record.delivery_cost) || 0;

            return deliveryCost > 0 ? `${deliveryCost.toFixed(2)} €` : '0 €';
          },
        },
        {
          title: '',
          key: 'action',
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              return '';
            }

            const getStatusInfo = (status: string | undefined) => {
              switch (status) {
                case 'selected':
                  return {
                    label: intl.formatMessage({
                      id: 'listings.applications.selected',
                    }),
                    className: 'selected',
                  };
                case 'accepted':
                  return {
                    label: intl.formatMessage({
                      id: 'listings.applications.accepted',
                    }),
                    className: 'accepted',
                  };
                case 'rejected':
                  return {
                    label: intl.formatMessage({
                      id: 'listings.applications.rejected',
                    }),
                    className: 'rejected',
                  };
                case 'withdrawn':
                  return {
                    label: intl.formatMessage({
                      id: 'listings.applications.withdrawn',
                    }),
                    className: 'withdrawn',
                  };
                default:
                  return null;
              }
            };

            const statusInfo = getStatusInfo(record.status);

            return (
              <div className="action-cell">
                {statusInfo && (
                  <Tag className={`status-tag status ${statusInfo.className}`}>
                    {statusInfo.label}
                  </Tag>
                )}
                <Button
                  className="view-button"
                  label={
                    ['selected', 'accepted', 'rejected', 'withdrawn'].includes(
                      record.status || ''
                    )
                      ? intl.formatMessage({ id: 'listing.open' })
                      : intl.formatMessage({ id: 'listings.applications.view' })
                  }
                  onClick={() => {
                    const isStillSelectable = !(
                      (record.status === 'accepted' &&
                        record.order_transaction) ||
                      record.status === 'withdrawn' ||
                      listing?.status === 'completed'
                    );
                    setSelectedApplicationForView(
                      isStillSelectable
                        ? { ...record, openProductsTab: true }
                        : record
                    );
                    setViewDrawerOpen(true);
                  }}
                />
              </div>
            );
          },
        },
      ];
    } else {
      return [
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.category',
          }),
          key: 'category',
          width: '30%',
          sorter: (a: TreeTableRow, b: TreeTableRow) => {
            if (a.isProductRow || b.isProductRow) return 0;
            const aName = (a.category_name || '').toLowerCase();
            const bName = (b.category_name || '').toLowerCase();

            return aName.localeCompare(bName);
          },
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              return (
                <Flex align="center" gap={8}>
                  <Checkbox
                    checked={record.isSelected}
                    disabled={
                      (record.status === 'accepted' &&
                        record.order_transaction) ||
                      record.status === 'withdrawn' ||
                      listing?.status === 'completed' ||
                      (!!record.offer_valid_until &&
                        dayjs(record.offer_valid_until).isBefore(dayjs()))
                        ? true
                        : false
                    }
                    onChange={(e) => {
                      setSelectedProducts((prev) => {
                        const newData = [...prev];

                        if (e.target.checked) {
                          const existingIndex = newData.findIndex(
                            (s) =>
                              s.listing_application_id ===
                                record.application_id &&
                              s.listing_application_product_id ===
                                record.listing_application_product_id
                          );

                          if (existingIndex === -1) {
                            const quantity =
                              record.offered_quantity ??
                              (typeof record.listing_product
                                ?.required_quantity === 'string'
                                ? parseFloat(
                                    record.listing_product?.required_quantity
                                  ) || 0
                                : record.listing_product?.required_quantity ||
                                  0);

                            newData.push({
                              id: Date.now(),
                              listing_application_id:
                                record.application_id || 0,
                              listing_application_product_id:
                                record.listing_application_product_id || 0,
                              selected_quantity: quantity,
                              listing_application_product: {
                                listing_product: {
                                  id: String(record.listing_product_id),
                                },
                              },
                            });
                          }
                        } else {
                          const filtered = newData.filter(
                            (s) =>
                              !(
                                s.listing_application_id ===
                                  record.application_id &&
                                s.listing_application_product_id ===
                                  record.listing_application_product_id
                              )
                          );
                          return filtered;
                        }

                        return newData;
                      });
                    }}
                  />
                  <span>{record.farm_brand || record.farm_name || ''}</span>
                </Flex>
              );
            }

            return record.category_name || '';
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.total_price',
          }),
          key: 'total_price',
          width: '15%',
          sorter: (a: TreeTableRow, b: TreeTableRow) => {
            if (a.isProductRow || b.isProductRow) return 0;

            const aTotalQuantity = (a.children || []).reduce(
              (sum: number, product: TreeTableRow) => {
                const requiredQty = product.listing_product?.required_quantity;
                const quantity =
                  product.offered_quantity !== null &&
                  product.offered_quantity !== undefined
                    ? product.offered_quantity
                    : typeof requiredQty === 'string'
                      ? parseFloat(requiredQty) || 0
                      : requiredQty || 0;
                const price = product.offered_price || 0;
                return sum + quantity * price;
              },
              0
            );

            const bTotalQuantity = (b.children || []).reduce(
              (sum: number, product: TreeTableRow) => {
                const requiredQty = product.listing_product?.required_quantity;
                const quantity =
                  product.offered_quantity !== null &&
                  product.offered_quantity !== undefined
                    ? product.offered_quantity
                    : typeof requiredQty === 'string'
                      ? parseFloat(requiredQty) || 0
                      : requiredQty || 0;
                const price = product.offered_price || 0;
                return sum + quantity * price;
              },
              0
            );

            return aTotalQuantity - bTotalQuantity;
          },
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              const requiredQty = record.listing_product?.required_quantity;
              const quantity =
                record.offered_quantity ??
                (typeof requiredQty === 'string'
                  ? parseFloat(requiredQty)
                  : requiredQty) ??
                0;
              const price = record.offered_price ?? 0;
              const totalPrice = quantity * price;
              const unitPrice = price;
              const unit = record.listing_product?.storage_unit?.short || '';

              const hasBothBestValues =
                record.isBestTotalPrice && record.isBestUnitPrice;

              return totalPrice > 0 ? (
                <CenteredCell>
                  {hasBothBestValues ? (
                    <div className="best-value-highlight">
                      <div>{totalPrice.toFixed(2)} €</div>
                      {unitPrice > 0 && unit && (
                        <div>
                          {unitPrice.toFixed(2)} €/{unit}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div
                        className={
                          record.isBestTotalPrice
                            ? 'best-value-highlight'
                            : undefined
                        }
                      >
                        {totalPrice.toFixed(2)} €
                      </div>
                      {unitPrice > 0 && unit && (
                        <div
                          className={
                            record.isBestUnitPrice
                              ? 'best-value-highlight'
                              : undefined
                          }
                        >
                          {unitPrice.toFixed(2)} €/{unit}
                        </div>
                      )}
                    </>
                  )}
                </CenteredCell>
              ) : (
                ''
              );
            }
            return '';
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.quantity',
          }),
          key: 'quantity',
          width: '15%',
          sorter: (a: TreeTableRow, b: TreeTableRow) => {
            if (a.isProductRow || b.isProductRow) return 0;

            const aTotalQuantity = (a.children || []).reduce(
              (sum: number, product: TreeTableRow) => {
                const requiredQty = product.listing_product?.required_quantity;
                const quantity =
                  product.offered_quantity !== null &&
                  product.offered_quantity !== undefined
                    ? product.offered_quantity
                    : typeof requiredQty === 'string'
                      ? parseFloat(requiredQty) || 0
                      : requiredQty || 0;

                return sum + quantity;
              },
              0
            );

            const bTotalQuantity = (b.children || []).reduce(
              (sum: number, product: TreeTableRow) => {
                const requiredQty = product.listing_product?.required_quantity;
                const quantity =
                  product.offered_quantity !== null &&
                  product.offered_quantity !== undefined
                    ? product.offered_quantity
                    : typeof requiredQty === 'string'
                      ? parseFloat(requiredQty) || 0
                      : requiredQty || 0;

                return sum + quantity;
              },
              0
            );

            return aTotalQuantity - bTotalQuantity;
          },
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              const requiredQty = record.listing_product?.required_quantity;
              const quantity =
                record.offered_quantity !== null &&
                record.offered_quantity !== undefined
                  ? record.offered_quantity
                  : typeof requiredQty === 'string'
                    ? parseFloat(requiredQty) || 0
                    : requiredQty || 0;
              const unit = record.listing_product?.storage_unit?.short || '';

              return quantity > 0 ? (
                <CenteredCell>
                  <span
                    className={
                      record.isBestQuantity ? 'best-value-highlight' : undefined
                    }
                  >
                    {quantity} {unit}
                  </span>
                </CenteredCell>
              ) : (
                ''
              );
            }

            const firstProduct = record.children?.[0];
            const requiredQty =
              firstProduct?.listing_product?.required_quantity;
            const quantity =
              typeof requiredQty === 'string'
                ? parseFloat(requiredQty) || 0
                : requiredQty || 0;
            const unit =
              firstProduct?.listing_product?.storage_unit?.short || '';

            if (quantity > 0) {
              return (
                <CenteredCell>
                  {quantity} {unit}
                </CenteredCell>
              );
            }

            return '';
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.distance',
          }),
          key: 'distance',
          width: '15%',
          render: (record: TreeTableRow) => {
            if (!record.isProductRow) return '';
            const numValue =
              typeof record.calculated_distance_km === 'string'
                ? parseFloat(record.calculated_distance_km)
                : record.calculated_distance_km;
            return numValue && !isNaN(numValue) ? (
              <CenteredCell>
                <span
                  className={
                    record.isBestDistance ? 'best-value-highlight' : undefined
                  }
                >
                  {numValue} km
                </span>
              </CenteredCell>
            ) : (
              ''
            );
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.contract',
          }),
          key: 'contract',
          width: '10%',
          render: (record: TreeTableRow) => {
            if (record.isProductRow) {
              if (record.hasContractChanges) {
                return (
                  <Button
                    type="link"
                    label={intl.formatMessage({
                      id: 'listings.applications.contract_changes',
                    })}
                    onClick={() => {
                      if (!record.application_id) {
                        return;
                      }
                      const application = applicationsMap.get(
                        record.application_id
                      );
                      if (!application) {
                        return;
                      }
                      setSelectedApplication(application);
                      setDrawerOpen(true);
                    }}
                    className="contract-changes-button"
                  />
                );
              }

              return (
                <Checkbox
                  checked={true}
                  disabled={true}
                  className="contract-checkbox"
                />
              );
            }

            return '';
          },
        },
        {
          title: intl.formatMessage({
            id: 'listings.applications.table.delivery_cost',
          }),
          key: 'delivery_cost',
          width: '15%',
          render: (record: TreeTableRow) => {
            if (!record.isProductRow) return '';
            const deliveryCost = Number(record.delivery_cost) || 0;
            return deliveryCost > 0 ? `${deliveryCost.toFixed(2)} €` : '0 €';
          },
        },
        {
          title: '',
          key: 'action',
          render: (record: TreeTableRow) => {
            if (!record.isProductRow) {
              return '';
            }

            const getStatusInfo = (
              status: string | undefined,
              isProductSelected: boolean
            ) => {
              switch (status) {
                case 'selected':
                case 'accepted':
                  if (!isProductSelected) return null;
                  return {
                    label: intl.formatMessage({
                      id:
                        status === 'selected'
                          ? 'listings.applications.selected'
                          : 'listings.applications.accepted',
                    }),
                    className: status,
                  };
                case 'rejected':
                  return {
                    label: intl.formatMessage({
                      id: 'listings.applications.rejected',
                    }),
                    className: 'rejected',
                  };
                case 'withdrawn':
                  return {
                    label: intl.formatMessage({
                      id: 'listings.applications.withdrawn',
                    }),
                    className: 'withdrawn',
                  };
                default:
                  return null;
              }
            };

            const statusInfo = getStatusInfo(
              record.status,
              record.isSelected ?? false
            );

            return (
              <div className="action-cell">
                {statusInfo && (
                  <Tag className={`status-tag status ${statusInfo.className}`}>
                    {statusInfo.label}
                  </Tag>
                )}
                <Button
                  className="view-button"
                  label={
                    statusInfo
                      ? intl.formatMessage({ id: 'listing.open' })
                      : intl.formatMessage({ id: 'listings.applications.view' })
                  }
                  onClick={() => {
                    const applicationRecord = {
                      id: record.application_id,
                      status: record.status,
                      delivery_cost: record.delivery_cost,
                      total_offer_price: record.total_offer_price,
                      order_transaction: record.order_transaction,
                      openProductsTab: true,
                    };
                    handleViewApplication?.(applicationRecord);
                  }}
                  size="small"
                />
              </div>
            );
          },
        },
      ];
    }
  }, [intl, hasContractChanges, isSingleProducerOnly, applicationsMap]);

  const treeData = useMemo(() => {
    const shouldHighlightBestValue = (
      value: number | null | undefined,
      type:
        | 'unitPrice'
        | 'totalPrice'
        | 'applicationTotalPrice'
        | 'distance'
        | 'quantity'
        | 'applicationQuantity'
        | 'productQuantity',
      productBestValuesOverride?: {
        minUnitPrice: number | null;
        minTotalPrice: number | null;
      }
    ): boolean => {
      if (!bestValues || !value || value <= 0) return false;

      const roundedValue = Math.round(value * 100) / 100;

      switch (type) {
        case 'unitPrice': {
          const minUnitPrice =
            productBestValuesOverride?.minUnitPrice ?? bestValues.minUnitPrice;
          const roundedMinUnitPrice =
            minUnitPrice != null ? Math.round(minUnitPrice * 100) / 100 : null;
          return (
            roundedMinUnitPrice !== null && roundedValue === roundedMinUnitPrice
          );
        }
        case 'totalPrice': {
          const minTotalPrice =
            productBestValuesOverride?.minTotalPrice ??
            bestValues.minTotalPrice;
          const roundedMinTotalPrice =
            minTotalPrice != null
              ? Math.round(minTotalPrice * 100) / 100
              : null;
          return (
            roundedMinTotalPrice !== null &&
            roundedValue === roundedMinTotalPrice
          );
        }
        case 'applicationTotalPrice':
          const roundedMinApplicationTotalPrice =
            bestValues.minApplicationTotalPrice
              ? Math.round(bestValues.minApplicationTotalPrice * 100) / 100
              : null;
          return (
            roundedMinApplicationTotalPrice !== null &&
            roundedValue === roundedMinApplicationTotalPrice
          );
        case 'distance':
          return (
            bestValues.minDistance !== null &&
            roundedValue === bestValues.minDistance
          );
        case 'applicationQuantity':
          return (
            bestValues.maxApplicationQuantity !== null &&
            roundedValue === bestValues.maxApplicationQuantity
          );
        case 'productQuantity':
          return (
            bestValues.maxProductQuantity !== null &&
            roundedValue === bestValues.maxProductQuantity
          );
        case 'quantity':
          return (
            bestValues.maxProductQuantity !== null &&
            roundedValue === bestValues.maxProductQuantity
          );
        default:
          return false;
      }
    };

    if (isSingleProducerOnly) {
      return applications.map((application) => {
        const children: TreeTableRow[] =
          application.application_products?.map(
            (product: ExtendedApplicationProductData) => {
              const unitPrice = product.offered_price ?? 0;

              const requiredQty = product.listing_product?.required_quantity;
              const quantity =
                product.offered_quantity ??
                (typeof requiredQty === 'string'
                  ? parseFloat(requiredQty)
                  : requiredQty) ??
                0;

              const totalPrice = quantity * unitPrice;

              const listingProductId = String(
                product.listing_product_id ?? product.listing_product?.id ?? ''
              );
              const perProductQuantity =
                bestValues?.productQuantityByListingProduct?.[listingProductId];
              const isBestQuantity =
                perProductQuantity != null &&
                quantity === perProductQuantity.maxQuantity;
              const productBestValues =
                bestValues?.productBestValuesByListingProduct?.[
                  listingProductId
                ];

              const productRow = {
                ...product,
                id: `product-${product.id}`,
                isProductRow: true,
                product_name:
                  product.listing_product?.name ||
                  product.listing_product?.category?.name ||
                  '',
                offered_quantity: product.offered_quantity,
                offered_price: product.offered_price || 0,
                listing_product: product.listing_product,
                offer_valid_until: application.offer_valid_until,
                isBestUnitPrice: shouldHighlightBestValue(
                  unitPrice,
                  'unitPrice',
                  productBestValues
                ),
                isBestTotalPrice: shouldHighlightBestValue(
                  totalPrice,
                  'totalPrice',
                  productBestValues
                ),
                isBestQuantity,
                isBestDistance: false,
              };

              return productRow;
            }
          ) || [];

        const appTotalPrice =
          typeof application.total_offer_price === 'string'
            ? parseFloat(application.total_offer_price)
            : application.total_offer_price || 0;

        const appTotalQuantity = (
          application.application_products || []
        ).reduce((sum, product: ExtendedApplicationProductData) => {
          const requiredQty = product.listing_product?.required_quantity;
          const quantity =
            product.offered_quantity ??
            (typeof requiredQty === 'string'
              ? parseFloat(requiredQty)
              : requiredQty) ??
            0;
          return sum + quantity;
        }, 0);

        const appDistance =
          typeof application.calculated_distance_km === 'string'
            ? parseFloat(application.calculated_distance_km)
            : application.calculated_distance_km || 0;

        const appRow = {
          ...application,
          children: !!children.length ? children : undefined,
          calculated_distance_km: application.calculated_distance_km,
          isBestApplicationTotalPrice: shouldHighlightBestValue(
            appTotalPrice,
            'applicationTotalPrice'
          ),
          isBestQuantity: shouldHighlightBestValue(
            appTotalQuantity,
            'applicationQuantity'
          ),
          isBestDistance: shouldHighlightBestValue(appDistance, 'distance'),
        } as TreeTableRow;

        return appRow;
      });
    } else {
      const categoryMap = new Map<string, TreeTableRow>();
      const categoryBestValues = new Map<
        string,
        {
          minUnitPrice: number | null;
          maxUnitPrice: number | null;
          minTotalPrice: number | null;
          maxTotalPrice: number | null;
          minDistance: number | null;
          maxDistance: number | null;
          minQuantity: number | null;
          maxQuantity: number | null;
        }
      >();

      applications.forEach((application) => {
        application.application_products?.forEach(
          (product: ExtendedApplicationProductData) => {
            const categoryName = product.listing_product?.category?.name || '';
            const categoryId = `category-${product.listing_product?.category?.id || categoryName}`;

            if (!categoryMap.has(categoryId)) {
              categoryMap.set(categoryId, {
                id: categoryId,
                category_name: categoryName,
                isCategoryRow: true,
                children: [],
              } as TreeTableRow);
              categoryBestValues.set(categoryId, {
                minUnitPrice: null,
                maxUnitPrice: null,
                minTotalPrice: null,
                maxTotalPrice: null,
                minDistance: null,
                maxDistance: null,
                minQuantity: null,
                maxQuantity: null,
              });
            }

            const unitPrice = product.offered_price ?? 0;
            const requiredQty = product.listing_product?.required_quantity;
            const quantity =
              product.offered_quantity ??
              (typeof requiredQty === 'string'
                ? parseFloat(requiredQty)
                : requiredQty) ??
              0;
            const totalPrice = quantity * unitPrice;
            const productDistance =
              typeof application.calculated_distance_km === 'string'
                ? parseFloat(application.calculated_distance_km)
                : application.calculated_distance_km || 0;

            const bestValues = categoryBestValues.get(categoryId)!;
            if (unitPrice > 0) {
              if (
                bestValues.minUnitPrice === null ||
                unitPrice < bestValues.minUnitPrice
              ) {
                bestValues.minUnitPrice = unitPrice;
              }
              if (
                bestValues.maxUnitPrice === null ||
                unitPrice > bestValues.maxUnitPrice
              ) {
                bestValues.maxUnitPrice = unitPrice;
              }
            }
            if (totalPrice > 0) {
              if (
                bestValues.minTotalPrice === null ||
                totalPrice < bestValues.minTotalPrice
              ) {
                bestValues.minTotalPrice = totalPrice;
              }
              if (
                bestValues.maxTotalPrice === null ||
                totalPrice > bestValues.maxTotalPrice
              ) {
                bestValues.maxTotalPrice = totalPrice;
              }
            }
            if (productDistance > 0) {
              if (
                bestValues.minDistance === null ||
                productDistance < bestValues.minDistance
              ) {
                bestValues.minDistance = productDistance;
              }
              if (
                bestValues.maxDistance === null ||
                productDistance > bestValues.maxDistance
              ) {
                bestValues.maxDistance = productDistance;
              }
            }
            if (quantity > 0) {
              if (
                bestValues.minQuantity === null ||
                quantity < bestValues.minQuantity
              ) {
                bestValues.minQuantity = quantity;
              }
              if (
                bestValues.maxQuantity === null ||
                quantity > bestValues.maxQuantity
              ) {
                bestValues.maxQuantity = quantity;
              }
            }
          }
        );
      });

      applications.forEach((application) => {
        application.application_products?.forEach(
          (product: ExtendedApplicationProductData) => {
            const categoryName = product.listing_product?.category?.name || '';
            const categoryId = `category-${product.listing_product?.category?.id || categoryName}`;
            const categoryRow = categoryMap.get(categoryId)!;
            const bestValues = categoryBestValues.get(categoryId)!;

            const productKey = `product|${product.listing_product_id}|${application.id}`;
            const isSelected = selectedProducts.some(
              (s) =>
                s.listing_application_id === application.id &&
                s.listing_application_product_id === product.id
            );

            const unitPrice = product.offered_price ?? 0;
            const requiredQty = product.listing_product?.required_quantity;
            const quantity =
              product.offered_quantity ??
              (typeof requiredQty === 'string'
                ? parseFloat(requiredQty)
                : requiredQty) ??
              0;
            const totalPrice = quantity * unitPrice;
            const productDistance =
              typeof application.calculated_distance_km === 'string'
                ? parseFloat(application.calculated_distance_km)
                : application.calculated_distance_km || 0;

            const roundedUnitPrice = Math.round(unitPrice * 100) / 100;
            const roundedTotalPrice = Math.round(totalPrice * 100) / 100;
            const roundedDistance = Math.round(productDistance * 100) / 100;
            const roundedQuantity = Math.round(quantity * 100) / 100;

            const roundedMinUnitPrice =
              bestValues.minUnitPrice !== null
                ? Math.round(bestValues.minUnitPrice * 100) / 100
                : null;
            const roundedMaxUnitPrice =
              bestValues.maxUnitPrice !== null
                ? Math.round(bestValues.maxUnitPrice * 100) / 100
                : null;
            const isBestUnitPrice =
              roundedMinUnitPrice !== null &&
              roundedMaxUnitPrice !== null &&
              roundedMinUnitPrice !== roundedMaxUnitPrice &&
              roundedUnitPrice === roundedMinUnitPrice;

            const roundedMinTotalPrice =
              bestValues.minTotalPrice !== null
                ? Math.round(bestValues.minTotalPrice * 100) / 100
                : null;
            const roundedMaxTotalPrice =
              bestValues.maxTotalPrice !== null
                ? Math.round(bestValues.maxTotalPrice * 100) / 100
                : null;
            const isBestTotalPrice =
              roundedMinTotalPrice !== null &&
              roundedMaxTotalPrice !== null &&
              roundedMinTotalPrice !== roundedMaxTotalPrice &&
              roundedTotalPrice === roundedMinTotalPrice;

            const roundedMinDistance =
              bestValues.minDistance !== null
                ? Math.round(bestValues.minDistance * 100) / 100
                : null;
            const roundedMaxDistance =
              bestValues.maxDistance !== null
                ? Math.round(bestValues.maxDistance * 100) / 100
                : null;
            const isBestDistance =
              roundedMinDistance !== null &&
              roundedMaxDistance !== null &&
              roundedMinDistance !== roundedMaxDistance &&
              roundedDistance === roundedMinDistance;

            const isBestQuantity =
              bestValues.minQuantity !== null &&
              bestValues.maxQuantity !== null &&
              bestValues.minQuantity !== bestValues.maxQuantity &&
              roundedQuantity ===
                Math.round(bestValues.maxQuantity * 100) / 100;

            const hasProductChanges = application.application_products?.some(
              (appProduct) => appProduct.offered_quantity !== null
            );
            const listingHasStructuredDelivery =
              listing?.delivery_conditions?.some(
                (c: { delivery_type?: string }) =>
                  c.delivery_type === 'delivery_conditions' ||
                  c.delivery_type === 'detailed_delivery'
              ) ?? false;
            const hasDeliveryChanges = listingHasStructuredDelivery
              ? application.delivery_conditions?.some(
                  (delivery) =>
                    delivery.is_accepting_all === false ||
                    delivery.listing_delivery_condition_id === null
                )
              : false;
            const hasContractChangesValue = !!(
              hasProductChanges || hasDeliveryChanges
            );

            categoryRow.children!.push({
              ...product,
              id: productKey,
              isProductRow: true,
              product_name: product.listing_product?.name || categoryName,
              offered_quantity: product.offered_quantity,
              offered_price: product.offered_price || 0,
              listing_product: product.listing_product,
              farm_name: application.farm_name,
              farm_brand: application.farm_brand,
              application_id: application.id,
              listing_product_id: product.listing_product_id,
              listing_application_product_id: product.id,
              status: application.status,
              delivery_cost: application.delivery_cost,
              total_offer_price: application.total_offer_price,
              order_transaction: application.order_transaction,
              applicant_farm_id: application.applicant_farm_id,
              submitted_at: application.submitted_at,
              calculated_distance_km: application.calculated_distance_km,
              isSelected,
              isBestUnitPrice,
              isBestTotalPrice,
              isBestQuantity,
              isBestDistance,
              hasContractChanges: hasContractChangesValue,
              offer_valid_until: application.offer_valid_until,
            } as TreeTableRow);
          }
        );
      });

      return [...Array.from(categoryMap.values())];
    }
  }, [
    applications,
    isSingleProducerOnly,
    selectedProducts,
    bestValues,
    listing,
  ]);

  const renderSingleProducerMobile = () => (
    <StyledMobileApplicationCards>
      <Spinner spinning={!!loading}>
        {applications.map((app) => (
          <div key={app.id} className="application-card single-producer-card">
            <div className="card-header">
              <span className="card-label">
                {intl.formatMessage({
                  id: 'listings.applications.table.producer',
                })}
              </span>
              <span className="card-value">
                {app.farm_brand || app.farm_name || '-'}
              </span>
            </div>
            <div className="card-row">
              <div className="card-field">
                <span className="card-label">
                  {intl.formatMessage({
                    id: 'listings.applications.table.total_price',
                  })}
                </span>
                <span className="card-value">
                  {(() => {
                    const numValue =
                      typeof app.total_offer_price === 'string'
                        ? parseFloat(app.total_offer_price)
                        : app.total_offer_price;
                    return numValue && !isNaN(numValue)
                      ? `${numValue.toFixed(2)} €`
                      : '-';
                  })()}
                </span>
              </div>
              <div className="card-field end">
                <span className="card-label">
                  {intl.formatMessage({ id: 'form.distance' })}
                </span>
                <span className="card-value">
                  {(() => {
                    const numValue =
                      typeof app.calculated_distance_km === 'string'
                        ? parseFloat(app.calculated_distance_km)
                        : app.calculated_distance_km;
                    return numValue && !isNaN(numValue) ? `${numValue}km` : '-';
                  })()}
                </span>
              </div>
            </div>
            <div className="card-row">
              <div className="card-field">
                <span className="card-label">
                  {intl.formatMessage({
                    id: 'listings.applications.table.contract',
                  })}
                </span>
                {hasContractChanges(app as TreeTableRow) ? (
                  <Button
                    type="link"
                    label={
                      <>
                        {intl.formatMessage({
                          id: 'listings.applications.contract_changes',
                        })}
                        <img src="/svgs/admin/open-link-icon.svg" alt="" />
                      </>
                    }
                    onClick={() => {
                      setSelectedApplication(app as TreeTableRow);
                      setDrawerOpen(true);
                    }}
                    className="contract-changes-button"
                  />
                ) : (
                  <Checkbox checked={true} disabled={true} />
                )}
              </div>
              <div className="card-field end">
                <span className="card-label">
                  {intl.formatMessage({ id: 'delivery.delivery' })}
                </span>
                <span className="card-value">
                  {Number(app.delivery_cost) > 0
                    ? `${Number(app.delivery_cost).toFixed(2)} EUR`
                    : '0 EUR'}
                </span>
              </div>
            </div>
            {app.application_products &&
              app.application_products.length > 0 && (
                <div className="card-products">
                  {app.application_products.map(
                    (product: ExtendedApplicationProductData) => {
                      const productName =
                        product.listing_product?.name ||
                        product.listing_product?.category?.name ||
                        '';
                      const requiredQty =
                        product.listing_product?.required_quantity;
                      const quantity =
                        product.offered_quantity ??
                        (typeof requiredQty === 'string'
                          ? parseFloat(requiredQty)
                          : requiredQty) ??
                        0;
                      const unitPrice = product.offered_price ?? 0;
                      const totalPrice = quantity * unitPrice;
                      const unit =
                        product.listing_product?.storage_unit?.short || '';

                      return (
                        <div key={product.id} className="product-item">
                          <span className="product-name">{productName}</span>
                          <div className="card-row">
                            <div className="card-field">
                              <span className="card-label">
                                {intl.formatMessage({
                                  id: 'listings.applications.table.total_price',
                                })}
                              </span>
                              <span className="card-value">
                                {totalPrice > 0
                                  ? `${totalPrice.toFixed(2)} € - ${unitPrice.toFixed(2)} €/${unit}`
                                  : '-'}
                              </span>
                            </div>
                            <div className="card-field end">
                              <span className="card-label">
                                {intl.formatMessage({
                                  id: 'listings.applications.table.quantity',
                                })}
                              </span>
                              <span className="card-value">
                                {quantity > 0 ? `${quantity} ${unit}` : '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            <Button
              className="view-application-btn"
              label={intl.formatMessage({ id: 'listings.applications.view' })}
              onClick={() => {
                const isStillSelectable = !(
                  (app.status === 'accepted' && app.order_transaction) ||
                  app.status === 'withdrawn' ||
                  listing?.status === 'completed'
                );
                setSelectedApplicationForView(
                  isStillSelectable
                    ? ({ ...app, openProductsTab: true } as TreeTableRow)
                    : (app as TreeTableRow)
                );
                setViewDrawerOpen(true);
              }}
            />
          </div>
        ))}
      </Spinner>
    </StyledMobileApplicationCards>
  );

  const handleMobileCheckboxChange = (
    record: TreeTableRow,
    checked: boolean
  ) => {
    setSelectedProducts((prev) => {
      const newData = [...prev];
      if (checked) {
        const existingIndex = newData.findIndex(
          (s) =>
            s.listing_application_id === record.application_id &&
            s.listing_application_product_id ===
              record.listing_application_product_id
        );
        if (existingIndex === -1) {
          const quantity =
            record.offered_quantity ??
            (typeof record.listing_product?.required_quantity === 'string'
              ? parseFloat(record.listing_product?.required_quantity) || 0
              : record.listing_product?.required_quantity || 0);
          newData.push({
            id: Date.now(),
            listing_application_id: record.application_id || 0,
            listing_application_product_id:
              record.listing_application_product_id || 0,
            selected_quantity: quantity,
            listing_application_product: {
              listing_product: { id: String(record.listing_product_id) },
            },
          });
        }
      } else {
        return newData.filter(
          (s) =>
            !(
              s.listing_application_id === record.application_id &&
              s.listing_application_product_id ===
                record.listing_application_product_id
            )
        );
      }
      return newData;
    });
  };

  const renderMultiProducerMobile = () => (
    <StyledMobileApplicationCards>
      <ApplicationsInfographics
        applications={applications}
        listing={listing || null}
        selectedProducts={selectedProducts}
        savedSelectedProducts={savedSelectedProducts}
        splitDeliveries={splitDeliveries}
        isSingleProducerOnly={isSingleProducerOnly}
        onViewApplication={handleViewApplication}
        selectProducts={selectProducts}
        onCreateTransactions={onCreateTransactions}
      />
      <Spinner spinning={!!loading}>
        {(treeData || []).map((category) => (
          <div key={category.id} className="application-card">
            <div className="card-row product-header-row">
              <span className="card-label">
                {intl.formatMessage({ id: 'general.products' })}
              </span>
              <span className="card-label">
                {intl.formatMessage({
                  id: 'listings.applications.table.quantity',
                })}
              </span>
            </div>
            <div className="card-row product-name-row">
              <span className="card-value product-name">
                {category.category_name || '-'}
              </span>
              <span className="card-value">
                {(() => {
                  const firstChild = category.children?.[0];
                  const reqQty = firstChild?.listing_product?.required_quantity;
                  const unit =
                    firstChild?.listing_product?.storage_unit?.short || '';
                  return reqQty ? `${reqQty} ${unit}` : '';
                })()}
              </span>
            </div>
            {category.children?.map((child: TreeTableRow) => {
              const requiredQty = child.listing_product?.required_quantity;
              const quantity =
                child.offered_quantity ??
                (typeof requiredQty === 'string'
                  ? parseFloat(requiredQty)
                  : requiredQty) ??
                0;
              const unitPrice = child.offered_price ?? 0;
              const totalPrice = quantity * unitPrice;
              const unit = child.listing_product?.storage_unit?.short || '';
              const distance =
                typeof child.calculated_distance_km === 'string'
                  ? parseFloat(child.calculated_distance_km)
                  : child.calculated_distance_km;
              const isDisabled =
                (child.status === 'accepted' && child.order_transaction) ||
                child.status === 'withdrawn' ||
                listing?.status === 'completed' ||
                (!!child.offer_valid_until &&
                  dayjs(child.offer_valid_until).isBefore(dayjs()));

              return (
                <div key={child.id} className="farm-offer-section">
                  <div className="farm-name-row">
                    <Checkbox
                      checked={child.isSelected}
                      disabled={!!isDisabled}
                      onChange={(e) =>
                        handleMobileCheckboxChange(child, e.target.checked)
                      }
                    />
                    <span className="card-value farm-name">
                      {child.farm_brand || child.farm_name || '-'}
                    </span>
                  </div>
                  <div className="card-row">
                    <div className="card-field">
                      <span className="card-label">
                        {intl.formatMessage({
                          id: 'listings.applications.table.total_price',
                        })}
                      </span>
                      <span className="card-value">
                        {totalPrice > 0
                          ? `${totalPrice.toFixed(2)} € - ${unitPrice.toFixed(2)} €/${unit}`
                          : '-'}
                      </span>
                    </div>
                    <div className="card-field end">
                      <span className="card-label">
                        {intl.formatMessage({
                          id: 'listings.applications.table.quantity',
                        })}
                      </span>
                      <span className="card-value">
                        {quantity > 0 ? `${quantity} ${unit}` : '-'}
                      </span>
                    </div>
                  </div>
                  <div className="card-row">
                    <div className="card-field">
                      <span className="card-label">
                        {intl.formatMessage({ id: 'form.distance' })}
                      </span>
                      <span className="card-value">
                        {distance && !isNaN(distance) ? `${distance} km` : '-'}
                      </span>
                    </div>
                    <div className="card-field end">
                      <span className="card-label">
                        {intl.formatMessage({ id: 'delivery.delivery' })}
                      </span>
                      <span className="card-value">
                        {Number(child.delivery_cost) > 0
                          ? `${Number(child.delivery_cost).toFixed(2)} EUR`
                          : '0 EUR'}
                      </span>
                    </div>
                  </div>
                  <div className="card-row">
                    <div className="card-field">
                      <span className="card-label">
                        {intl.formatMessage({
                          id: 'listings.applications.table.contract',
                        })}
                      </span>
                      {child.hasContractChanges ? (
                        <Button
                          type="link"
                          label={
                            <>
                              {intl.formatMessage({
                                id: 'listings.applications.contract_changes',
                              })}
                              <img
                                src="/svgs/admin/open-link-icon.svg"
                                alt=""
                              />
                            </>
                          }
                          onClick={() => {
                            setSelectedApplication(child);
                            setDrawerOpen(true);
                          }}
                          className="contract-changes-button"
                        />
                      ) : (
                        <Checkbox checked={true} disabled={true} />
                      )}
                    </div>
                  </div>
                  <Button
                    className="view-application-btn"
                    label={intl.formatMessage({
                      id: 'listings.applications.view',
                    })}
                    onClick={() => {
                      const app = applicationsMap.get(
                        child.application_id as number
                      );
                      if (app) {
                        setSelectedApplicationForView({
                          id: app.id,
                          status: app.status,
                          delivery_cost: app.delivery_cost,
                          total_offer_price: app.total_offer_price,
                          order_transaction: app.order_transaction,
                          openProductsTab: true,
                        });
                      } else {
                        const isStillSelectable = !(
                          (child.status === 'accepted' &&
                            child.order_transaction) ||
                          child.status === 'withdrawn' ||
                          listing?.status === 'completed'
                        );
                        setSelectedApplicationForView(
                          isStillSelectable
                            ? { ...child, openProductsTab: true }
                            : child
                        );
                      }
                      setViewDrawerOpen(true);
                    }}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </Spinner>
    </StyledMobileApplicationCards>
  );

  return (
    <>
      {!isSingleProducerOnly ? (
        <ResponsiveWrapper
          desktopComponent={
            <StyledPageLayout>
              <StyledInfographicsContainer>
                <ApplicationsInfographics
                  applications={applications}
                  listing={listing || null}
                  selectedProducts={selectedProducts}
                  savedSelectedProducts={savedSelectedProducts}
                  splitDeliveries={splitDeliveries}
                  isSingleProducerOnly={isSingleProducerOnly}
                  onViewApplication={handleViewApplication}
                  selectProducts={selectProducts}
                  onCreateTransactions={onCreateTransactions}
                />
              </StyledInfographicsContainer>
              <StyledTableContainer>
                <StyledApplicationsTable
                  columns={columns}
                  dataSource={treeData}
                  rowKey="id"
                  loading={loading}
                  filter={filter}
                  page_size={page_size}
                  childrenColumnName="children"
                  expandedRowKeys={expandedRowKeys}
                  onExpandedRowsChange={onExpandedRowsChange}
                  disablePagination={applications.length <= 10}
                  rowClassName={(record) =>
                    (record as TreeTableRow).isCategoryRow
                      ? 'category-row'
                      : 'supplier-row'
                  }
                />
              </StyledTableContainer>
            </StyledPageLayout>
          }
          mobileComponent={renderMultiProducerMobile()}
        />
      ) : (
        <ResponsiveWrapper
          desktopComponent={
            <StyledApplicationsTable
              columns={columns}
              dataSource={treeData}
              rowKey="id"
              loading={loading}
              filter={filter}
              page_size={page_size}
              childrenColumnName="children"
              expandedRowKeys={expandedRowKeys}
              onExpandedRowsChange={onExpandedRowsChange}
              disablePagination={applications.length <= 10}
              rowClassName={(record) =>
                (record as TreeTableRow).isCategoryRow
                  ? 'category-row'
                  : 'supplier-row'
              }
            />
          }
          mobileComponent={renderSingleProducerMobile()}
        />
      )}
      <DrawerShowListingContractChanges
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedApplication(null);
        }}
        closable={true}
        application={selectedApplication}
        listing={listing}
      />
      <DrawerViewListingApplication
        open={viewDrawerOpen}
        onClose={() => {
          setViewDrawerOpen(false);
          setSelectedApplicationForView(null);
        }}
        application={selectedApplicationForView}
        listing={listing}
        applications={applications}
        onActionCompleted={onActionCompleted}
        onRefetchSelectedProducts={refetchSelectedProducts}
        zIndex={1200}
        defaultActiveTab={
          selectedApplicationForView &&
          'openProductsTab' in selectedApplicationForView &&
          selectedApplicationForView.openProductsTab
            ? 'products'
            : undefined
        }
        selectionMode={Boolean(
          selectedApplicationForView &&
            'openProductsTab' in selectedApplicationForView &&
            selectedApplicationForView.openProductsTab
        )}
        selectedProductsData={selectedProducts}
        selectProducts={selectProducts}
      />
    </>
  );
};
